import * as XLSX from 'xlsx';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getLocalizedText as getText } from './localizationHelpers';

/**
 * Fetch user data for given user IDs
 * @param {Array<string>} userIds - Array of user IDs
 * @returns {Promise<Object>} Map of userId to user data
 */
const fetchUserData = async (userIds) => {
  const userMap = {};
  const uniqueIds = [...new Set(userIds.filter(Boolean))];
  
  await Promise.all(
    uniqueIds.map(async (userId) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          userMap[userId] = userDoc.data();
        }
      } catch (error) {
        console.error(`Error fetching user ${userId}:`, error);
      }
    })
  );
  
  return userMap;
};

/**
 * Format proof submissions into readable string
 * @param {Array} proofs - Array of proof submissions
 * @returns {string}
 */
const formatProofs = (proofs) => {
  if (!proofs || !Array.isArray(proofs) || proofs.length === 0) {
    return 'No proofs';
  }
  
  return proofs.map((proof, index) => {
    const label = getText(proof.label, `Proof ${index + 1}`);
    const value = proof.value || '—';
    const type = proof.inputType || 'text';
    
    if (proof.storagePath) {
      return `${label}: [${type}] ${value}`;
    }
    return `${label}: ${value}`;
  }).join('\n');
};

/**
 * Fetch quest completions from Firestore
 * @param {string} status - Filter by status ('approved', 'rejected', 'pending', or 'all')
 * @param {string} questId - Optional quest ID to filter by specific quest
 * @returns {Promise<Array>}
 */
export const fetchCompletedQuests = async (status = 'approved', questId = null) => {
  try {
    const completionsCollectionRef = collection(db, 'questCompletions');
    
    let completionsQuery;
    if (questId) {
      // Filter by both status and questId
      if (status === 'all') {
        completionsQuery = query(
          completionsCollectionRef,
          where('questId', '==', questId)
        );
      } else {
        completionsQuery = query(
          completionsCollectionRef,
          where('questId', '==', questId),
          where('status', '==', status)
        );
      }
    } else {
      // Filter by status only
      if (status === 'all') {
        completionsQuery = completionsCollectionRef;
      } else {
        completionsQuery = query(
          completionsCollectionRef,
          where('status', '==', status)
        );
      }
    }
    
    const querySnapshot = await getDocs(completionsQuery);
    
    const completions = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return completions.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
  } catch (error) {
    console.error('Error fetching completed quests:', error);
    throw error;
  }
};

/**
 * Export quest completions to Excel file
 * @param {Array} completions - Array of quest completion objects
 * @param {Object} userMap - Map of userId to user data
 * @param {string} filename - Optional filename (without extension)
 */
export const exportQuestCompletionsToExcel = (completions, userMap, filename = null) => {
  try {
    // Get quest title and side quests from the first completion (assuming all completions are for the same quest)
    const questTitle = completions.length > 0 ? getText(completions[0].questData?.title, 'Untitled Quest') : 'Quest Completions';
    const sideQuests = completions.length > 0 ? (completions[0].questData?.sideQuests || []) : [];
    
    const excelData = completions.map(completion => {
      const questData = completion.questData || {};
      const mainQuestXP = questData.value || 0;
      const sideQuestsXP = (questData.sideQuests || []).reduce((sum, sq) => sum + (sq.value || 0), 0);
      const totalPossibleXP = mainQuestXP + sideQuestsXP;
      
      const user = userMap[completion.userId];
      const reviewer = userMap[completion.reviewedBy];
      
      const reviewerName = reviewer?.twitchUsername || reviewer?.username || '—';
      const reviewerId = completion.reviewedBy || '';
      const reviewedByDisplay = reviewerId ? `${reviewerName} (${reviewerId})` : '—';
      
      // Extract and format approved items from reviewNotes
      let approvedItems = '';
      if (completion.reviewNotes?.includes('Approved items:')) {
        const itemsText = completion.reviewNotes.replace('Approved items:', '').trim();
        const items = itemsText.split(',').map(item => item.trim());
        
        const formattedItems = items.map(item => {
          if (item === 'main quest') {
            return 'Main Quest';
          } else if (item.startsWith('side quest: ')) {
            // Extract just the quest name, remove "side quest: " prefix
            return item.replace('side quest: ', '');
          }
          return item;
        });
        
        approvedItems = formattedItems.join('\n');
      }
      
      const row = {
        'Username': user?.twitchUsername || user?.username || '—',
        'User ID': completion.userId || '',
        'Total Possible XP': totalPossibleXP,
        'XP Awarded': completion.totalXpAwarded !== undefined ? completion.totalXpAwarded : mainQuestXP,
        'Submitted Date': completion.submittedAt ? new Date(completion.submittedAt).toLocaleString() : '',
        'Reviewed Date': completion.reviewedAt ? new Date(completion.reviewedAt).toLocaleString() : '',
        'Reviewed By': reviewedByDisplay,
        'Approved Items': approvedItems,
        'Main Quest': formatProofs(completion.proofSubmissions),
      };
      
      // Add each side quest as its own column
      sideQuests.forEach((sideQuest, index) => {
        const sideQuestTitle = getText(sideQuest.title, `Side Quest ${index + 1}`);
        const sideQuestXP = sideQuest.value || 0;
        const columnName = `${sideQuestTitle} (${sideQuestXP})`;
        
        // Get the proof submissions for this specific side quest
        const sideQuestProofs = completion.sideQuestProofSubmissions?.[index];
        row[columnName] = sideQuestProofs ? formatProofs(sideQuestProofs) : '—';
      });
      
      row['Revision Count'] = completion.revisionCount || 1;
      
      return row;
    });
    
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Add quest title as header in cell A1
    if (!worksheet['A1']) {
      worksheet['A1'] = { t: 's', v: '' };
    }
    
    // Insert title row at the top
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const newRange = { s: { c: 0, r: 0 }, e: { c: range.e.c, r: range.e.r + 1 } };
    worksheet['!ref'] = XLSX.utils.encode_range(newRange);
    
    // Shift existing data down by 1 row
    for (let row = range.e.r; row >= 0; row--) {
      for (let col = 0; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const newCellAddress = XLSX.utils.encode_cell({ r: row + 1, c: col });
        if (worksheet[cellAddress]) {
          worksheet[newCellAddress] = worksheet[cellAddress];
          delete worksheet[cellAddress];
        }
      }
    }
    
    // Add title in A1
    worksheet['A1'] = { t: 's', v: questTitle };
    worksheet['A1'].s = { font: { bold: true, sz: 14 } };
    
    // Merge cells for title (span across all columns)
    if (!worksheet['!merges']) worksheet['!merges'] = [];
    worksheet['!merges'].push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: range.e.c }
    });
    
    // Calculate column widths based on content
    const calculateColumnWidth = (data, columnIndex) => {
      let maxLength = 0;
      data.forEach(row => {
        const cellValue = row[Object.keys(row)[columnIndex]];
        if (cellValue) {
          // Count newlines and calculate width accordingly
          const lines = String(cellValue).split('\n');
          const maxLineLength = Math.max(...lines.map(line => line.length));
          maxLength = Math.max(maxLength, maxLineLength);
        }
      });
      // Add some padding and ensure minimum width
      return Math.max(maxLength + 2, 10);
    };

    // Set column widths based on content
    const baseColumnWidths = [
      { wch: calculateColumnWidth(excelData, 0) }, // Username
      { wch: calculateColumnWidth(excelData, 1) }, // User ID
      { wch: calculateColumnWidth(excelData, 2) }, // Total Possible XP
      { wch: calculateColumnWidth(excelData, 3) }, // XP Awarded
      { wch: calculateColumnWidth(excelData, 4) }, // Submitted Date
      { wch: calculateColumnWidth(excelData, 5) }, // Reviewed Date
      { wch: calculateColumnWidth(excelData, 6) }, // Reviewed By
      { wch: calculateColumnWidth(excelData, 7) }, // Approved Items
      { wch: calculateColumnWidth(excelData, 8) }, // Main Quest
    ];
    
    // Add column widths for each side quest
    const sideQuestColumnWidths = sideQuests.map((_, index) => ({
      wch: calculateColumnWidth(excelData, 9 + index)
    }));
    
    const columnWidths = [
      ...baseColumnWidths,
      ...sideQuestColumnWidths,
      { wch: calculateColumnWidth(excelData, 9 + sideQuests.length) }  // Revision Count
    ];
    
    worksheet['!cols'] = columnWidths;
    
    // Enable text wrapping and auto-fit row heights
    const cellRange = XLSX.utils.decode_range(worksheet['!ref']);
    for (let row = 1; row <= cellRange.e.r; row++) {
      for (let col = 0; col <= cellRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = {
            ...worksheet[cellAddress].s,
            alignment: {
              wrapText: true,
              vertical: 'top'
            }
          };
        }
      }
    }
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Quest Completions');
    
    const date = new Date().toISOString().split('T')[0];
    const finalFilename = filename || `quest_completions_${date}.xlsx`;
    
    XLSX.writeFile(workbook, finalFilename);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw error;
  }
};

/**
 * Main export function
 * @param {string} status - Status filter ('approved', 'rejected', 'pending', 'all')
 * @param {string} filename - Optional filename
 * @param {string} questId - Optional quest ID to filter by specific quest
 * @returns {Promise<number>} Number of completions exported
 */
export const exportQuestCompletionsReport = async (status = 'approved', filename = null, questId = null) => {
  try {
    const completions = await fetchCompletedQuests(status, questId);
    
    if (completions.length === 0) {
      const questText = questId ? ' for this quest' : '';
      throw new Error(`No ${status === 'all' ? '' : status} quest completions found${questText}`);
    }
    
    // Collect all unique user IDs (submitters and reviewers)
    const userIds = [];
    completions.forEach(completion => {
      if (completion.userId) userIds.push(completion.userId);
      if (completion.reviewedBy) userIds.push(completion.reviewedBy);
    });
    
    // Fetch user data
    const userMap = await fetchUserData(userIds);
    
    exportQuestCompletionsToExcel(completions, userMap, filename);
    
    return completions.length;
  } catch (error) {
    console.error('Error generating quest completions report:', error);
    throw error;
  }
};

